/**
 * Audio graph — v0M15.4.
 *
 * Owns the shared `AudioContext` and a master `GainNode` that all SFX
 * playback routes through. Settings-storage feeds volume + mute changes
 * here in real time (via `subscribeSettings` in `bootstrap`), so the
 * user hears slider drags as they happen.
 *
 * Prior to v0M15.4, `battle-effects.ts` owned the AudioContext directly
 * and connected each SFX to `ctx.destination` with a hard-coded per-SFX
 * gain. That path is now:
 *
 *     osc → per-SFX gain → master gain → destination
 *
 * The per-SFX gain still shapes the envelope (attack / decay); the
 * master gain applies the SFX volume slider (multiplicative, 0..1).
 * When `muted === true`, master gain is forced to 0 regardless of
 * slider value.
 *
 * Music: no music system exists at v0M15.4. The music-volume slider in
 * the Settings modal is persisted but has no consumer yet; when a music
 * loop is added it should mount its source through a second gain node
 * exposed by `getMusicGain()` that this module updates identically.
 */

import { loadSettings } from './settings-storage';
import type { Settings } from './settings-storage';

let audioCtx: AudioContext | null = null;
let sfxMasterGain: GainNode | null = null;
let audioInitFailed = false;

/**
 * Return the shared AudioContext, creating it lazily on first call.
 * Returns null in environments without Web Audio (tests, SSR) and
 * caches the failure so we don't re-attempt every call.
 *
 * Note: the browser suspends fresh AudioContexts until the first user
 * gesture. Battle-effects's `playSfx` naturally happens after the
 * player has clicked to start the game, so this is fine — but if a
 * future caller wants to play audio before any interaction, they'll
 * need to gate on `resume()`.
 */
export function ensureAudioContext(): AudioContext | null {
  if (audioCtx !== null) return audioCtx;
  if (audioInitFailed) return null;
  if (
    typeof window === 'undefined' ||
    !('AudioContext' in window || 'webkitAudioContext' in (window as unknown as Record<string, unknown>))
  ) {
    audioInitFailed = true;
    return null;
  }
  try {
    const Ctor =
      (window as unknown as { AudioContext: typeof AudioContext }).AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioCtx = new Ctor();

    // Build the SFX master gain and route it to destination. Initial
    // gain reflects current settings — the audio graph is live from the
    // moment it exists so an SFX fired immediately after context
    // creation respects settings without a warm-up frame.
    sfxMasterGain = audioCtx.createGain();
    sfxMasterGain.connect(audioCtx.destination);
    applySettingsToGain(loadSettings());

    return audioCtx;
  } catch {
    audioInitFailed = true;
    return null;
  }
}

/**
 * Return the master gain node that SFX should route their output into
 * (in place of `ctx.destination`). Returns null if the audio context
 * failed to initialise — callers should treat that as "audio disabled"
 * and skip playback rather than falling back to `ctx.destination`
 * (which would bypass the settings-driven volume + mute).
 */
export function getSfxMasterGain(): GainNode | null {
  ensureAudioContext();
  return sfxMasterGain;
}

/**
 * Apply the current settings (or a freshly-changed settings snapshot)
 * to the audio graph. Called from `bootstrap()` at startup and by the
 * settings-storage subscribe callback whenever the user drags a slider.
 * Cheap: single `setValueAtTime` on the master gain.
 */
export function applyAudioSettings(s: Settings): void {
  applySettingsToGain(s);
}

function applySettingsToGain(s: Settings): void {
  if (sfxMasterGain === null || audioCtx === null) return;
  const effective = s.audio.muted ? 0 : s.audio.sfxVolume;
  // Use setValueAtTime rather than direct .value = X so the change is
  // sample-accurate at the current time — avoids audible clicks when
  // dragging the slider fast during playback.
  sfxMasterGain.gain.setValueAtTime(effective, audioCtx.currentTime);
}

/** Test helper — reset internal state between tests. */
export function _resetAudio(): void {
  audioCtx = null;
  sfxMasterGain = null;
  audioInitFailed = false;
}
